<script setup lang="ts">
import PickList, {
    type PickListMoveAllToSourceEvent,
    type PickListMoveAllToTargetEvent,
    type PickListMoveToSourceEvent,
    type PickListMoveToTargetEvent,
} from 'primevue/picklist';
import { computed } from 'vue';
import type { BaseCollection } from '@/models/BaseCollection';
import type { BaseModel } from '@/models/BaseModel';

const sourceCollection = defineModel<BaseCollection<BaseModel>>('sourceCollection', { default: [] });
const targetCollection = defineModel<BaseCollection<BaseModel>>('targetCollection', { default: [] });

const props = withDefaults(
    defineProps<{
        sourceIdField?: string;
        sourceDisplayField: string;
        targetIdField?: string;
        targetDisplayField: string;
        defaultTartgetProps: { [key: string]: any };
    }>(),
    {
        sourceIdField: 'id',
        targetIdField: 'id',
    },
);

const emit = defineEmits<{
    (e: 'remove', value: BaseModel): void;
}>();

type PicklistProps = {
    id: number;
    text: string;
};

const selectionMap = computed(() => {
    const targetSelection: PicklistProps[] = [];
    targetCollection.value?.each((model) => {
        targetSelection.push({
            id: model.get(props.targetIdField),
            text: model.get(props.targetDisplayField),
        } as PicklistProps);
    });

    const sourceSelection: PicklistProps[] = [];
    sourceCollection.value?.each((model) => {
        if (!targetSelection.find((m) => m.id === model.get(props.sourceIdField))) {
            sourceSelection.push({
                id: model.get(props.sourceIdField),
                text: model.get(props.sourceDisplayField),
            } as PicklistProps);
        }
    });

    return [sourceSelection, targetSelection];
});

function addItems(values: PicklistProps[]) {
    values.forEach((value) => {
        const model: BaseModel = targetCollection.value.createNewModel();
        model.set(props.defaultTartgetProps);
        model.set(props.targetIdField, value.id);
        model.set(props.targetDisplayField, value.text);
        targetCollection.value.push(model);
    });
}

function removeItems(values: PicklistProps[]) {
    values.forEach((value) => {
        const found = targetCollection.value.find((model) => model.get(props.targetIdField) === value.id);
        if (found) {
            targetCollection.value.remove(found);
            emit('remove', found);
        }
    });
}

function moveToTarget(event: PickListMoveToTargetEvent) {
    addItems(event.items);
}

function moveAllToTarget(event: PickListMoveAllToTargetEvent) {
    addItems(event.items);
}

function moveToSource(event: PickListMoveToSourceEvent) {
    removeItems(event.items);
}

function moveAllToSource(event: PickListMoveAllToSourceEvent) {
    removeItems(event.items);
}
</script>
<template>
    <div>
        <PickList
            :model-value="selectionMap"
            dataKey="id"
            :showSourceControls="false"
            :showTargetControls="false"
            @move-to-target="moveToTarget"
            @move-all-to-target="moveAllToTarget"
            @move-to-source="moveToSource"
            @move-all-to-source="moveAllToSource"
        >
            <template #option="{ option }">
                {{ $t(option.text) }}
            </template>
            <template #sourceheader
                ><b>{{ $t('verfuegbare_werte') }}</b></template
            >
            <template #targetheader
                ><b>{{ $t('ausgewaelte_werte') }}</b></template
            >
        </PickList>
    </div>
</template>
